import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Loader2, MessageSquare, ArrowLeft, AlertTriangle, Menu } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useChatWithPosit } from "@/hooks/useChatWithPosit";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";

/**
 * CHAT WITH POSIT - Main Conversational AI Interface
 * 
 * This is the core feature where users interact with AI-powered knowledge bases.
 * 
 * KEY FUNCTIONALITY:
 * 1. Vector Search: Uses Pinecone to find relevant knowledge for user queries
 * 2. Contextual AI: OpenAI generates responses based on Posit content
 * 3. Access Control: Handles public, private, and paid Posit access
 * 4. Real-time Chat: Manages conversation flow and message history
 * 5. Payment Integration: Processes subscriptions for monetized Posits
 * 
 * AI PROCESSING FLOW:
 * User Query → Vector Search (Pinecone) → Context Assembly → 
 * OpenAI Response → Formatted Output → UI Display
 * 
 * ACCESS PATTERNS:
 * - Public Posits: Anyone can access and chat
 * - Private Posits: Owner only
 * - Monetized Posits: Subscription required
 * - Anonymous Users: Limited preview mode
 * 
 * VECTOR INTEGRATION:
 * Each message triggers a search in the user's Pinecone namespace to find
 * the most relevant knowledge chunks for generating contextual responses.
  */

import { updateMetaTags } from "@/utils/meta";
import { useEffect, useState } from "react";
import { AuthDialog } from "@/components/posit/AuthDialog";
import PreviewLimitBanner from "@/components/posit/PreviewLimitBanner";
import PositDeleted from "@/components/posit/PositDeleted";
import ChatContainer from "@/components/chat/interface/ChatContainer";
import { ChatInterfaceProvider, useChatInterface } from "@/contexts/ChatInterfaceContext";
import ChatLayout from "@/components/chat/layout/ChatLayout";
import { Posit } from "@/types/posit";
import PaywallModal from "@/components/posit/PaywallModal";
import CreatorOnboardingModal from "@/components/posit/CreatorOnboardingModal";
import PositInactiveModal from "@/components/posit/PositInactiveModal";
import InactivePositBanner from "@/components/posit/InactivePositBanner";
import { usePositValidation } from "@/hooks/usePositValidation";
import { trackEvent, ANALYTICS_EVENTS } from "@/utils/analytics";
import { capture } from "@/lib/posthog";
import InvalidPositIdError from "@/components/debug/InvalidPositIdError";
import { ChatErrorBoundary } from "@/components/chat/ChatErrorBoundary";

// Enhanced type for posit data with access information
interface EnhancedPositData extends Posit {
  ownerName: string;
  hasAccess: boolean;
  isOwner: boolean;
  isPaidPosit?: boolean;
  creator_handle?: string;
}

const INACTIVITY_THRESHOLD = 2 * 60 * 1000; // 2 minutes in milliseconds

const ChatWithPositInner = () => {
  const { user, isLoading: authLoading } = useAuth();
  const { positId: rawPositId, chatId: conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const demoQuestion = searchParams.get('demoQuestion');
  const { toast } = useToast();
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [showCreatorOnboarding, setShowCreatorOnboarding] = useState(false);
  const [lastActivityTime, setLastActivityTime] = useState(Date.now());
  const { setCurrentPosit, welcomeMode, toggleSidebar } = useChatInterface();
  const isMobile = useIsMobile();

  // Validate posit ID and get clean version
  const { isValidPositId, positId } = usePositValidation(rawPositId);

  // Get positData with enhanced access information
  const { data: positData, isLoading: isLoadingPosit, error: positError } = useQuery({
    queryKey: ["posit-with-access", positId, user?.id],
    queryFn: async () => {
      if (!positId) throw new Error("No posit ID provided");

      try {
        const { data: ownedPosit, error: ownedError } = await supabase
          .from("posits")
          .select(`
            *,
            users!fk_posits_user_id (
              first_name,
              last_name,
              email,
              handle
            )
          `)
          .eq('id', positId)
          .single();

        if (ownedError) {
          // Handle the case where posit is not found gracefully
          if (ownedError.code === 'PGRST116') {
            throw new Error(`Posit not found: ${positId}`);
          }
          throw ownedError;
        }

        // Generate owner name with improved fallback logic
        let ownerName = "Creator";
        if (ownedPosit?.users) {
          const userInfo = Array.isArray(ownedPosit.users) ? ownedPosit.users[0] : ownedPosit.users;
          if (userInfo?.first_name?.trim()) {
            ownerName = userInfo.last_name?.trim() 
              ? `${userInfo.first_name.trim()} ${userInfo.last_name.trim()}`
              : userInfo.first_name.trim();
          } else if (userInfo?.email?.includes('@')) {
            // Extract name from email with better formatting
            const emailPart = userInfo.email.split('@')[0];
            if (emailPart.includes('.')) {
              const nameParts = emailPart.split('.');
              ownerName = nameParts.map(part => 
                part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
              ).join(' ');
            } else {
              ownerName = emailPart.charAt(0).toUpperCase() + emailPart.slice(1).toLowerCase();
            }
          }
        }

        const isOwner = user?.id === ownedPosit?.user_id;
        
        // Check access for paid posits and inactive posits
        const positAny = ownedPosit as any; // Type assertion for now
        const isPaidPosit = positAny?.monetization_enabled === true || positAny?.is_paid === true;
        const isActivePosit = positAny?.is_active !== false; // Default to true if undefined
        
        // IMPORTANT: Inactive posits are not accessible to ANYONE (including owners)
        let hasAccess = false;
        
        if (!isActivePosit) {
          // Completely block access to inactive posits
          hasAccess = false;
        } else if (isOwner) {
          // Owner has access to their own active posits
          hasAccess = true;
        } else if (isPaidPosit) {
          // For paid posits, we need to check subscription/purchase status
          // For now, deny access - the PaywallModal will handle the purchase flow
          hasAccess = false;
        } else {
          // Free posits are accessible to everyone
          hasAccess = true;
        }

        // Get creator handle with same logic as name resolution
        const userInfo = Array.isArray(ownedPosit.users) ? ownedPosit.users[0] : ownedPosit.users;
        const creator_handle = userInfo?.handle || null; // Don't use fallback 'creator'

        // Transform the data to match the expected Posit type structure
        const transformedPosit = {
          ...ownedPosit,
          ownerName,
          hasAccess,
          isOwner,
          isPaidPosit,
          creator_handle,
          content: ownedPosit?.content_full || '', // Map content_full to content
          response_type: ownedPosit?.response_type || 'assistant', // Ensure response_type is present
          users: userInfo ? {
            first_name: userInfo.first_name,
            last_name: userInfo.last_name,
            handle: userInfo.handle,
            profile_image_url: undefined
          } : undefined
        };

        return transformedPosit as EnhancedPositData;
      } catch (error) {
        throw error;
      }
    },
    enabled: !!positId && isValidPositId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Set current posit in context for the WelcomeScreen
  useEffect(() => {
    if (positData) {
      setCurrentPosit(positData);
    }
  }, [positData, setCurrentPosit]);

  // Get chat functionality
  const {
    messages,
    content,
    setContent,
    isLoading: isChatLoading,
    handleQuery,
    refetchMessages,
  } = useChatWithPosit(positData || null, conversationId);

  // Get context functions (no longer used for message sync)
  const { setWelcomeMode, setIsLoading } = useChatInterface();

  // Sync welcome mode only (removed message sync to prevent duplicates)
  useEffect(() => {
    if (messages.length === 0) {
      setWelcomeMode(true);
    } else {
      setWelcomeMode(false);
    }
  }, [messages.length, setWelcomeMode]);

  // Sync loading state
  useEffect(() => {
    setIsLoading(isChatLoading);
  }, [isChatLoading, setIsLoading]);

  // Handle demo question from URL
  useEffect(() => {
    if (demoQuestion && !content && !isChatLoading && positData) {
      const decodedQuestion = decodeURIComponent(demoQuestion);
      setContent(decodedQuestion);
      
      const timer = setTimeout(() => {
        handleQuery(decodedQuestion);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [demoQuestion, content, setContent, handleQuery, isChatLoading, positData]);

  // Handle exit chat
  const handleExitChat = () => {
    // Get the referrer from browser
    const referrer = document.referrer;
    
    // Check if referrer is from within our app (same origin)
    const isInternalReferrer = referrer && 
      referrer.includes(window.location.origin) &&
      !referrer.includes('/chat/'); // Avoid circular navigation back to chat
    
    if (isInternalReferrer) {
      try {
        // Extract the path from referrer and navigate there
        const referrerUrl = new URL(referrer);
        const referrerPath = referrerUrl.pathname;
        
        // Additional safety checks for valid internal routes
        const validInternalRoutes = [
          '/dashboard',
          '/p/',
          '/public-posits',
          '/chat-history',
          '/creator-dashboard',
          '/bug-reports',
          '/posits',
          '/analytics'
        ];
        
        const isValidRoute = validInternalRoutes.some(route => 
          referrerPath.startsWith(route) || referrerPath === '/'
        );
        
        if (isValidRoute) {
          navigate(referrerPath);
          return;
        }
      } catch (error) {
        // Fall through to default behavior
      }
    }
    
    // Fallback to current behavior for external referrers or no referrer
    if (user) {
      // If user is logged in, go to their dashboard
      navigate("/dashboard");
    } else {
      // If user is not logged in, go to the posit creator's page
      const creatorHandle = positData.creator_handle;
      if (creatorHandle) {
        navigate(`/p/${creatorHandle}`);
      } else {
        // Fallback to public posits if no creator handle
        navigate("/public-posits");
      }
    }
  };

  // Update last activity time when user interacts
  const updateLastActivityTime = () => {
    setLastActivityTime(Date.now());
  };

  // Check for inactivity
  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() - lastActivityTime >= INACTIVITY_THRESHOLD) {
        if (!user && messages.length > 0) {
          setShowAuthDialog(true);
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(interval);
  }, [lastActivityTime, user, messages.length]);

  // Update meta tags
  useEffect(() => {
    if (positData) {
              updateMetaTags(positData.id);
    }
  }, [positData, positId]);

  // Loading states
  if (authLoading || isLoadingPosit) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Invalid posit ID
  if (!isValidPositId) {
    return (
      <InvalidPositIdError positId={rawPositId} />
    );
  }

  // Error states
  if (positError) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center p-6 max-w-md">
          <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Posit Not Found</h2>
          <p className="text-muted-foreground mb-4">
            {positError.message || "This posit doesn't exist or is no longer available."}
          </p>
          <div className="space-x-2">
            <Button onClick={() => navigate('/public-posits')}>
              Browse Public Posits
            </Button>
            {user && (
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Go to Dashboard
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Posit not found
  if (!positData) {
    return <PositDeleted />;
  }

  // No access
  if (!positData.hasAccess) {
    // Check if this is an inactive posit
    const positAny = positData as any;
    const isActivePosit = positAny?.is_active !== false;
    
    if (!isActivePosit) {
      // For inactive posits, show a dedicated page instead of modal over chat
      return (
        <div className="h-screen flex items-center justify-center bg-background">
          {/* Back Arrow Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => window.history.length > 1 ? window.history.back() : navigate("/public-posits")}
            className="absolute top-4 left-4 flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          
          <div className="max-w-md mx-auto text-center p-6">
            <div className="w-16 h-16 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="h-8 w-8 text-orange-600 dark:text-orange-400" />
            </div>
            
            <h1 className="text-2xl font-bold mb-3">Posit Currently Inactive</h1>
            <p className="text-muted-foreground mb-6">
              This Posit has been temporarily deactivated by its owner and is not available for chat.
            </p>
            
            <div className="bg-muted rounded-lg p-4 mb-6">
              <h3 className="font-semibold mb-2">"{positData.title}"</h3>
              <p className="text-sm text-muted-foreground">by {positData.ownerName}</p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={() => navigate(`/p/${positData.creator_handle}`)}
                className="w-full"
              >
                View Creator's Profile
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/public-posits")}
                className="w-full"
              >
                Browse Other Posits
              </Button>
            </div>
          </div>
        </div>
      );
    }
    
    // Check if this is a paid posit that needs the paywall modal
    if (positData.isPaidPosit) {
      // If user is not authenticated, show login gate for preview
      if (!user) {
        return (
          <div className="h-screen flex items-center justify-center bg-background">
            <div className="text-center p-6 max-w-md">
              <h2 className="text-2xl font-semibold mb-2">Sign in to try 3 free questions</h2>
              <p className="text-muted-foreground mb-6">This Posit requires a subscription. Sign in to use your free preview.</p>
              <div className="flex gap-3 justify-center">
                <Button asChild>
                  <Link to={`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`}>
                    Sign In
                  </Link>
                </Button>
                <Button variant="outline" onClick={() => navigate('/public-posits')}>Browse Public Posits</Button>
              </div>
            </div>
          </div>
        );
      }
      const handlePurchase = async () => {
        const accessToken = (await supabase.auth.getSession()).data.session?.access_token;
        const successUrl = `${window.location.origin}/dashboard?subscription=success`;
        const cancelUrl = window.location.href;

        const handleKnownMessages = (msg: string) => {
          if (
            msg.includes('Connect onboarding') ||
            msg.includes('not completed') ||
            msg.includes('Creator has not completed')
          ) {
            setShowCreatorOnboarding(true);
            return true;
          }
          if (
            msg.includes('No authorization header provided') ||
            msg.includes('Invalid authentication')
          ) {
            throw new Error('Please sign in to subscribe.');
          }
          return false;
        };

        const extractMessage = (err: any): string => {
          if (!err) return '';
          const ctx: any = err.context;
          return (ctx && (ctx.error || ctx.message)) || err.message || '';
        };

        let checkoutUrl: string | undefined;

        try {
          const primary = await supabase.functions.invoke('create-revenue-split-checkout', {
            body: { 
              posit_id: positId,
              creator_id: positData.user_id,
              amount_cents: (positData.subscription_price || 5) * 100,
              payment_type: 'subscription',
              success_url: successUrl,
              cancel_url: cancelUrl
            },
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
          });

          if (primary.error || !primary.data?.checkout_url) {
            const msg = extractMessage(primary.error);
            if (msg && handleKnownMessages(msg)) return;
            throw new Error(msg || 'Checkout failed');
          }
          checkoutUrl = primary.data.checkout_url;
        } catch (primaryThrown: any) {
          const msg = extractMessage(primaryThrown);
          if (msg && handleKnownMessages(msg)) return;

          try {
            const fallback = await supabase.functions.invoke('create-posit-checkout', {
              body: {
                posit_id: positId,
                amount_cents: (positData.subscription_price || 5) * 100,
                success_url: successUrl,
                cancel_url: cancelUrl
              },
              headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined
            });

            if (fallback.error || !fallback.data?.checkout_url) {
              const fbMsg = extractMessage(fallback.error) || 'Checkout failed';
              throw new Error(fbMsg);
            }
            checkoutUrl = fallback.data.checkout_url;
          } catch (fallbackThrown: any) {
            const combined = extractMessage(fallbackThrown) || msg || 'Checkout failed';
            toast({ variant: 'destructive', title: 'Purchase Error', description: combined });
            return;
          }
        }

        if (checkoutUrl) {
          window.location.href = checkoutUrl;
        } else {
          toast({ variant: 'destructive', title: 'Purchase Error', description: 'No checkout URL received' });
        }
      };

      const handleCancel = () => {
        // Only redirect to creator profile if we have a valid handle
        if (positData.creator_handle) {
          navigate(`/p/${positData.creator_handle}`);
        } else {
          // Fallback to public posits page if no valid creator handle
          navigate('/public-posits');
        }
      };

      return (
        <>
          {/* Blurred background */}
          <div className="blur-sm pointer-events-none opacity-50">
            <ChatLayout 
              mobileTitle={`Chat with "${positData.title}"`}
              showMobileHeader={false}
            >
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
                  <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" className="gap-2">
                      <ArrowLeft className="h-4 w-4" />
                      Back
                    </Button>
                  </div>
                </div>
                <div className="flex-1 p-4">
                  <p className="text-muted-foreground">Loading chat...</p>
                </div>
              </div>
            </ChatLayout>
          </div>
          
          {/* Paywall modal or Creator onboarding modal */}
          {showCreatorOnboarding ? (
            <CreatorOnboardingModal
              posit={{
                id: positData.id,
                title: positData.title,
                subscription_price: positData.subscription_price,
                creator_name: positData.ownerName,
                creator_handle: positData.creator_handle,
                creator_first_name: positData.users?.first_name
              }}
              onCancel={() => {
                setShowCreatorOnboarding(false);
                handleCancel();
              }}
            />
          ) : (
            <PaywallModal 
              posit={{
                id: positData.id,
                title: positData.title,
                subscription_price: positData.subscription_price,
                creator_name: positData.ownerName,
                creator_handle: positData.creator_handle,
                creator_first_name: positData.users?.first_name
              }}
              onPurchase={handlePurchase}
              onCancel={handleCancel}
            />
          )}
        </>
      );
    }

    // For non-paid posits, show regular access denied
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">
            You don't have permission to access this Posit
          </p>
          <Button onClick={() => navigate("/dashboard")}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ChatErrorBoundary
      onError={(error, errorInfo) => {
        trackEvent(ANALYTICS_EVENTS.ERROR_OCCURRED, {
          error_type: 'chat_page_error',
          error_message: error.message,
          posit_id: positId,
          user_id: user?.id,
        });
      }}
    >
      <ChatLayout 
        mobileTitle={positData.title}
        showMobileHeader={false}
      >
        <div className="flex-1 overflow-hidden relative">
          {/* Show inactive banner for owners */}
          {positData.isOwner && (positData as any)?.is_active === false && (
            <InactivePositBanner positTitle={positData.title} />
          )}
          
          {/* Header - Only show on mobile */}
          {isMobile && (
            <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleExitChat}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
                <div className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  <h1 className="text-lg font-semibold">
                    {positData.title}
                  </h1>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleSidebar}
                  className="gap-2"
                >
                  <Menu className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Chat Container */}
          <div className={`${isMobile ? 'h-[calc(100%-64px)]' : 'h-full'}`}>
              <ChatContainer 
              messages={messages}
              isLoading={isChatLoading}
              onSendMessage={async (content: string) => {
                updateLastActivityTime();
                try {
                    // PostHog marketing event for funnel: first_chat when no prior messages
                    if (messages.length === 0) {
                      capture('first_chat', { posit_id: positData.id });
                    }
                  await handleQuery(content);
                } catch (err: any) {
                  const message = err?.message || '';
                  if (message.includes('LOGIN_REQUIRED')) {
                    setShowAuthDialog(true);
                    toast({ title: 'Login required', description: 'Login to use your 3 free preview questions.' });
                  } else if (message.includes('PREVIEW_LIMIT_EXCEEDED')) {
                    toast({ title: 'Preview limit reached', description: 'Subscribe to continue chatting.' });
                  } else if (message.includes('Too many requests') || message.includes('429')) {
                    toast({ title: 'Please slow down', description: 'You are sending messages too quickly.' });
                  } else {
                    // Show a user-friendly message instead of raw technical text
                    toast({ title: 'Something went wrong', description: 'We hit a snag. Please try again.' });
                  }
                }
              }}
              externalContent={content}
              onContentChange={setContent}
            />
          </div>

          {/* Auth Dialog */}
          <AuthDialog
            isOpen={showAuthDialog}
            onClose={() => setShowAuthDialog(false)}
            onSuccess={() => {
              setShowAuthDialog(false);
              toast({
                title: "Welcome!",
                description: "You can now continue your conversation.",
              });
            }}
          />
        </div>
      </ChatLayout>
    </ChatErrorBoundary>
  );
};

const ChatWithPosit = () => {
  return (
    <ChatInterfaceProvider>
      <ChatWithPositInner />
    </ChatInterfaceProvider>
  );
};

export default ChatWithPosit;
